import React from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import {
  DeskEmpty,
  DeskSearch,
  DeskTable,
  DeskTabs,
  DeskToolbar,
} from "../desktop/DeskTable";
import {
  InvoiceDueCell,
  ReceivablesAgingPanel,
  money,
} from "../invoice/invoiceDesktopParts";
import {
  formatInvoiceUpdatedAt,
  resolveInvoiceStatus,
} from "../../helpers/invoices";
import { getInvoiceCollectionStatus } from "../../helpers/invoiceDueStatus";

export const AR_STATUS_TABS = [
  { value: "sent", label: "Outstanding" },
  { value: "paid", label: "Paid" },
  { value: "all", label: "All" },
];

export const AR_SORT_OPTIONS = [
  { value: "due_date", label: "Due Date" },
  { value: "amount", label: "Amount" },
  { value: "client", label: "Client" },
  { value: "invoice_date", label: "Invoice Date" },
  { value: "status", label: "Status" },
];

/**
 * Desktop Accounts Receivable: KPI strip (collections snapshot), outstanding
 * tabs/search/sort toolbar, receivables table and the aging panel beside it.
 */
export function AccountsReceivableDesktop({
  onBack,
  loading,
  hubError,
  metrics,
  invoices,
  filtered,
  statusFilter,
  onStatusFilter,
  searchTerm,
  onSearch,
  sortKey,
  onSortChange,
  onOpen,
  onOpenStatement,
}) {
  const tabCounts = {
    sent: invoices.filter((i) => resolveInvoiceStatus(i.status) === "sent").length,
    paid: invoices.filter((i) => resolveInvoiceStatus(i.status) === "paid").length,
    all: invoices.length,
  };
  const tabs = AR_STATUS_TABS.map((tab) => ({ ...tab, count: tabCounts[tab.value] }));

  const columns = [
    {
      key: "invoice",
      label: "Invoice",
      width: "19%",
      primary: true,
      render: (invoice) => (
        <span className="dt-item__text">
          <span className="dt-strong">{invoice.invoice_number}</span>
          <span className="dt-sub">{invoice.projects?.project_name || invoice.title || "—"}</span>
        </span>
      ),
    },
    {
      key: "client",
      label: "Client",
      width: "18%",
      render: (invoice) => <span className="dt-strong">{invoice.clients?.client_name || "No client"}</span>,
    },
    {
      key: "sent",
      label: "Invoice Date",
      width: "16%",
      render: (invoice) => (
        <span className="dt-muted dt-nowrap">{formatInvoiceUpdatedAt(invoice.invoiceDate)}</span>
      ),
    },
    {
      key: "due",
      label: "Due",
      width: "19%",
      render: (invoice) => <InvoiceDueCell invoice={invoice} />,
    },
    {
      key: "status",
      label: "Status",
      width: "11%",
      render: (invoice) => (
        <span className="dt-strong">{invoice.paymentStateLabel || "—"}</span>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      align: "right",
      width: "17%",
      render: (invoice) => (
        <span className="dt-num">
          {money(
            resolveInvoiceStatus(invoice.status) === "sent"
              ? invoice.outstandingBalance
              : invoice.total_amount,
          )}
        </span>
      ),
    },
  ];

  return (
    <CrewPilotModuleShell
      title="Accounts Receivable"
      subtitle="Outstanding invoices and collections"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch
            value={searchTerm}
            onChange={onSearch}
            placeholder="Search invoices, clients, projects…"
            ariaLabel="Search receivables"
            width="380px"
          />
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Receivables summary"
        tiles={
          loading
            ? []
            : [
                { key: "open", value: money(metrics.outstandingReceivables), label: "Total Open", tone: "gold" },
                { key: "paid", value: money(metrics.paidThisMonth), label: "Paid This Month", tone: "green" },
                { key: "count", value: String(metrics.invoiceCount ?? 0), label: "Open Invoices", tone: "blue" },
                { key: "cust", value: String(metrics.customerCount ?? 0), label: "Customers With Balance", tone: "muted" },
              ]
        }
      />

      <div className="inv-grid">
        <div className="inv-main">
          <DeskToolbar>
            <DeskTabs options={tabs} value={statusFilter} onChange={onStatusFilter} ariaLabel="Filter receivables" />
            <button type="button" className="dt-btn" onClick={onOpenStatement}>
              Statement of Account
            </button>
            <span className="dt-toolbar__grow" />
            <label className="dt-select">
              <span>Sort by</span>
              <select value={sortKey} onChange={(event) => onSortChange(event.target.value)} aria-label="Sort receivables">
                {AR_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </DeskToolbar>

          {hubError ? <div className="dt-error">{hubError}</div> : null}

          {loading ? (
            <p className="inv-note">Loading receivables…</p>
          ) : filtered.length === 0 ? (
            <DeskEmpty title="No receivable invoices found" />
          ) : (
            <DeskTable
              testId="receivables-table"
              caption="Receivable invoices"
              columns={columns}
              rows={filtered}
              rowKey={(invoice) => invoice.invoice_id}
              onOpen={onOpen}
              openLabel={(invoice) => `Open ${invoice.invoice_number}`}
              rowClassName={(invoice) =>
                getInvoiceCollectionStatus(invoice) === "overdue" ? "is-overdue" : ""
              }
            />
          )}
        </div>

        <aside className="inv-side" aria-label="Receivables aging">
          <ReceivablesAgingPanel metrics={metrics} loading={loading} />
        </aside>
      </div>
    </CrewPilotModuleShell>
  );
}
